/**
 * HMLR Searches tab on the case file.
 *
 * Solicitors can:
 *   - Order Office Copies (OC1 / OC2) — pre-exchange title evidence
 *   - Run a Priority Search (OS1 / OS2) — locks 30-day priority before
 *     completion / charge registration
 *   - Run a Land Charges Bankruptcy search (K15 / K16) — required by
 *     most lenders before release of funds
 *
 * Results fulfil into a PDF attached to the case Documents → Searches
 * folder. Each search has a status timeline (pending → ordered →
 * completed) and OS1/OS2 expose the 30-working-day priority countdown.
 *
 * Provider note: all searches currently run via the local Mock provider
 * (PDF stamped "MOCK RESPONSE") until HMLR Business Gateway credentials
 * are populated under /settings/submissions.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MagnifyingGlass, FileText, Warning, CheckCircle, Plus, Trash, Bank, IdentificationCard, Clock, House, MapPin } from "@phosphor-icons/react";
import OrderOfficeCopiesDialog from "./post-completion/OrderOfficeCopiesDialog";

const STATUS_TONES = {
  pending:    { label: "Pending",    cls: "bg-slate-100 text-slate-700" },
  queued:     { label: "Queued",     cls: "bg-violet-100 text-violet-700" },
  ordered:    { label: "Ordered",    cls: "bg-blue-100 text-blue-700" },
  processing: { label: "Processing…", cls: "bg-indigo-100 text-indigo-700 animate-pulse" },
  received:   { label: "Received",   cls: "bg-amber-100 text-amber-800" },
  completed:  { label: "Completed",  cls: "bg-emerald-100 text-emerald-700" },
  failed:     { label: "Failed",     cls: "bg-rose-100 text-rose-700" },
  voided:     { label: "Voided",     cls: "bg-slate-200 text-slate-500 line-through" },
};

const CATEGORY_ICONS = {
  "Office Copies": FileText,
  "Priority Search": Clock,
  "Bankruptcy": IdentificationCard,
  "Index Map": MapPin,
};

function StatusPill({ status }) {
  const t = STATUS_TONES[status] || STATUS_TONES.pending;
  return (
    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${t.cls}`} data-testid={`search-status-${status}`}>
      {t.label}
    </span>
  );
}

function PriorityCountdown({ expiresAt }) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) {
    return <span className="text-[10px] font-semibold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">Priority expired</span>;
  }
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const tone = days <= 5 ? "text-rose-700 bg-rose-50" : days <= 10 ? "text-amber-700 bg-amber-50" : "text-emerald-700 bg-emerald-50";
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${tone}`} data-testid="search-priority-pill">
      Priority: {days} day{days === 1 ? "" : "s"} left
    </span>
  );
}

function OrderSearchDialog({ open, onOpenChange, catalogue, defaults, onCreated }) {
  const [kind, setKind] = useState("");
  const [titleNumber, setTitleNumber] = useState(defaults?.title_number || "");
  const [applicantName, setApplicantName] = useState(defaults?.applicant_name || "");
  // K15 / K16 — structured list of persons (surname + forenames each).
  // Starts with one empty row; solicitor adds more for joint borrowers.
  const [searchedPersons, setSearchedPersons] = useState([{ surname: "", forenames: "" }]);
  // SIM (Search of the Index Map) — expected number of titles disclosed,
  // drives the tiered fee (£8 for first 5, +£6 per 10 thereafter).
  const [simTitles, setSimTitles] = useState("1");
  // K15 (Full Search) — county/area to search + period (year range).
  const nowYear = new Date().getFullYear();
  const [counties, setCounties] = useState("");
  const [fromYear, setFromYear] = useState("1925");
  const [toYear, setToYear] = useState(String(nowYear));
  // Priority-search-specific fields (OS1 / OS2 only). Defaults are
  // pulled from the case where we have it so the form opens pre-filled.
  const today = new Date().toISOString().slice(0, 10);
  const [searchFromDate, setSearchFromDate] = useState(today);
  const [proprietorNames, setProprietorNames] = useState(defaults?.proprietor_names?.join(", ") || "");
  const [propertyAddress, setPropertyAddress] = useState(defaults?.property_address || "");
  const [applicationType, setApplicationType] = useState("");
  const [applicationTypeOther, setApplicationTypeOther] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Register snapshot for the title (proprietor + address as HMLR has
  // them). Loaded when an OS1/OS2 is being composed so we can pre-fill
  // and warn on divergence. Null = not yet fetched / no title chosen.
  const [titleInfo, setTitleInfo] = useState(null);
  const [titleInfoLoading, setTitleInfoLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setKind("");
      setTitleNumber(defaults?.title_number || "");
      setApplicantName(defaults?.applicant_name || "");
      setSearchFromDate(today);
      setProprietorNames(defaults?.proprietor_names?.join(", ") || "");
      setPropertyAddress(defaults?.property_address || "");
      setApplicationType("");
      setApplicationTypeOther("");
      setTitleInfo(null);
      setSearchedPersons([{ surname: "", forenames: "" }]);
      setSimTitles("1");
      setCounties("");
      setFromYear("1925");
      setToYear(String(nowYear));
    }
  }, [open, defaults, today, nowYear]);

  const meta = catalogue.find((c) => c.kind === kind);
  const needsTitle = ["oc1", "oc2", "os1", "os2"].includes(kind);
  const isBankruptcy = kind === "k15" || kind === "k16";
  const isFullSearch = kind === "k15";
  const isSim = kind === "sim";
  const simFee = (n) => {
    const t = Math.max(1, parseInt(n, 10) || 1);
    const base = meta?.fee ?? 8;
    return t <= 5 ? base : base + Math.ceil((t - 5) / 10) * 6;
  };
  const needsApplicantText = kind === "os1" || kind === "os2";  // "searching solicitor" — still a single field
  const isPriority = kind === "os1" || kind === "os2";

  /* When the user picks K15/K16 for the first time on a case that has
     clients, pre-fill the person rows from those clients so the
     solicitor confirms rather than types. Only fires if the current
     rows are all blank so we never clobber typed edits. */
  useEffect(() => {
    if (!isBankruptcy) return;
    const allBlank = searchedPersons.every((p) => !p.surname && !p.forenames && !p.company_name);
    if (allBlank && (defaults?.client_persons?.length || 0) > 0) {
      setSearchedPersons(defaults.client_persons.map((p) => ({ ...p })));
    }
  }, [isBankruptcy, defaults?.client_persons, searchedPersons]);

  /* Priority-search auto-fill: on a re-mortgage (application_type=charge)
     the case clients ARE the current proprietors, so pre-fill the
     proprietor field from them. NOT for "purchase" — the current
     proprietor is the seller, not our client. NOT for transfer_of_part
     either — could go either way and the solicitor should look at
     the register snapshot instead. */
  useEffect(() => {
    if (!isPriority) return;
    if (applicationType !== "charge") return;
    if (proprietorNames.trim()) return; // solicitor has already typed something
    const clientNames = (defaults?.client_persons || [])
      .map((p) => `${p.forenames} ${p.surname}`.trim())
      .filter(Boolean);
    if (clientNames.length === 0) return;
    setProprietorNames(clientNames.join(", "));
  }, [isPriority, applicationType, defaults?.client_persons, proprietorNames]);

  const updatePerson = (idx, field, value) => {
    setSearchedPersons((prev) => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };
  const addPerson = () => setSearchedPersons((prev) => [...prev, { surname: "", forenames: "" }]);
  const removePerson = (idx) => setSearchedPersons((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));

  /* Fetch title-info whenever the user picks an OS1/OS2 or changes
     the title number. Debounced via the request cancellation on
     re-render so quick typing doesn't fire a wave of requests. */
  useEffect(() => {
    if (!open || !isPriority || !defaults?.case_id) return;
    if (!titleNumber || titleNumber.length < 3) {
      setTitleInfo(null);
      return;
    }
    let cancelled = false;
    setTitleInfoLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await API.get(`/cases/${defaults.case_id}/title-info`, {
          params: { title_number: titleNumber },
        });
        if (cancelled) return;
        setTitleInfo(data);
      } catch {
        if (!cancelled) setTitleInfo(null);
      } finally {
        if (!cancelled) setTitleInfoLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, isPriority, titleNumber, defaults?.case_id]);

  /* Divergence detection — compare what the solicitor has typed against
     what HMLR has on the register. Case-insensitive, whitespace-tolerant.
     Returns the field names that differ so we can render targeted chips. */
  const registerProprietors = (titleInfo?.registered_proprietors || []).map((p) => (p.name || "").trim()).filter(Boolean);
  const typedProprietors = proprietorNames.split(",").map((s) => s.trim()).filter(Boolean);
  const proprietorsDiffer =
    registerProprietors.length > 0 && typedProprietors.length > 0 &&
    (
      typedProprietors.length !== registerProprietors.length ||
      typedProprietors.some((n, i) => (registerProprietors[i] || "").toLowerCase() !== n.toLowerCase())
    );
  const registerAddress = (titleInfo?.property_address || "").trim();
  const typedAddress = propertyAddress.trim();
  const addressDiffers =
    registerAddress && typedAddress &&
    registerAddress.toLowerCase().replace(/\s+/g, " ") !== typedAddress.toLowerCase().replace(/\s+/g, " ");

  const handleAutofillFromRegister = () => {
    if (!titleInfo) return;
    setProprietorNames((titleInfo.registered_proprietors || []).map((p) => p.name).join(", "));
    setPropertyAddress(titleInfo.property_address || "");
    toast.success("Filled from register");
  };

  const handleSubmit = async () => {
    if (!kind) return toast.error("Choose a search type");
    if (needsTitle && !titleNumber) return toast.error("Title number required for this search");
    if (needsApplicantText && !applicantName) return toast.error("Applicant name required for this search");
    let cleanedPersons = [];
    if (isBankruptcy) {
      cleanedPersons = searchedPersons
        .map((p) => (isFullSearch && p.type === "company")
          ? { type: "company", company_name: (p.company_name || "").trim() }
          : { type: "individual", surname: (p.surname || "").trim(), forenames: (p.forenames || "").trim() })
        .filter((p) => (p.type === "company" ? p.company_name : (p.surname || p.forenames)));
      if (cleanedPersons.length === 0) {
        return toast.error("Add at least one name to search");
      }
      for (let i = 0; i < cleanedPersons.length; i++) {
        const cp = cleanedPersons[i];
        if (cp.type === "company") {
          if (!cp.company_name) return toast.error(`Company ${i + 1}: company name is required`);
          continue;
        }
        if (!cp.surname) return toast.error(`Person ${i + 1}: surname is required`);
        if (!cp.forenames) return toast.error(`Person ${i + 1}: forename(s) required`);
      }
    }
    if (isFullSearch) {
      const cs = counties.split(",").map((s) => s.trim()).filter(Boolean);
      if (cs.length === 0) return toast.error("K15 needs at least one county / administrative area");
    }
    if (isPriority) {
      if (!searchFromDate) return toast.error("Search-from date required for priority searches");
      const proprietors = proprietorNames.split(",").map((s) => s.trim()).filter(Boolean);
      if (proprietors.length === 0) return toast.error("Enter at least one proprietor name");
      if (!propertyAddress.trim()) return toast.error("Property address required for priority searches");
      if (!applicationType) return toast.error("Select an application type");
      if (applicationType === "other" && !applicationTypeOther.trim()) {
        return toast.error("Describe the 'Other' application type");
      }
      // Soft-block on divergence — solicitor confirms once before lodging.
      if (proprietorsDiffer || addressDiffers) {
        const which = [];
        if (proprietorsDiffer) which.push("proprietor(s)");
        if (addressDiffers) which.push("property address");
        const proceed = window.confirm(
          `The ${which.join(" and ")} you've entered differ from the title register.\n\n` +
          `Lodging this priority search with details that don't match the register can void the priority entry.\n\n` +
          `Are you sure you want to proceed?`
        );
        if (!proceed) return;
      }
    }
    setSubmitting(true);
    try {
      const proprietors = proprietorNames.split(",").map((s) => s.trim()).filter(Boolean);
      const body = {
        kind,
        title_number: titleNumber || null,
        applicant_name: applicantName || null,
      };
      if (isBankruptcy) {
        body.searched_persons = cleanedPersons;
      }
      if (isFullSearch) {
        body.counties = counties.split(",").map((s) => s.trim()).filter(Boolean);
        body.from_year = fromYear.trim() || "1925";
        body.to_year = toYear.trim() || String(nowYear);
      }
      if (isSim) {
        body.disclosed_titles = Math.max(1, parseInt(simTitles, 10) || 1);
      }
      if (isPriority) {
        body.search_from_date = searchFromDate;
        body.proprietor_names = proprietors;
        body.property_address = propertyAddress.trim();
        body.application_type = applicationType;
        if (applicationType === "other") {
          body.application_type_other = applicationTypeOther.trim();
        }
      }
      const { data } = await API.post(`/cases/${defaults.case_id}/searches`, body);
      onCreated(data);
      onOpenChange(false);
      toast.success(
        kind === "os2"
          ? `${meta.label} added — attach a title/estate plan, then click "Fulfil"`
          : `${meta.label} added — click "Fulfil" to run`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create search");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="search-order-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Order an HMLR search</DialogTitle>
          <DialogDescription>
            Office Copies, Priority Searches, and Land Charges bankruptcy searches — all run through HMLR.
            <span className="block text-slate-500 mt-1 text-[11px]">Office Copies, Priority Searches (OS1/OS2), Land Charges (K15/K16) and the Index Map (SIM) run <strong>live against HM Land Registry</strong> when the Business Gateway is configured under <em>Settings → Submission Credentials</em> — otherwise a mock result is returned for testing.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Search type</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="h-9 text-sm" data-testid="search-kind-select"><SelectValue placeholder="Choose a search…" /></SelectTrigger>
              <SelectContent>
                {catalogue.map((c) => (
                  <SelectItem key={c.kind} value={c.kind}>
                    {c.label} — £{c.fee}{c.category === "Bankruptcy" ? " per name" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {needsTitle && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Title number</Label>
              <Input value={titleNumber} onChange={(e) => setTitleNumber(e.target.value.toUpperCase())} placeholder="e.g. NK123456" className="h-9 text-sm font-mono" data-testid="search-title-input" />
            </div>
          )}
          {needsApplicantText && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                Applicant name <span className="text-slate-400 font-normal normal-case">(searching solicitor)</span>
              </Label>
              <Input value={applicantName} onChange={(e) => setApplicantName(e.target.value)} placeholder="Full name as it appears on the lender's mortgage offer" className="h-9 text-sm" data-testid="search-applicant-input" />
            </div>
          )}
          {isBankruptcy && (
            <div className="space-y-2 border-t border-slate-200 pt-3 mt-1" data-testid="bankruptcy-persons-section">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">
                {isFullSearch ? "Names to search" : "People to search"} ({searchedPersons.length})
              </div>
              <p className="text-[11px] text-slate-500 -mt-1">
                {isFullSearch
                  ? "A K15 Full Search runs against each named individual or company over the chosen period. Use the toggle to switch a row between an individual and a company."
                  : "K16 searches HMLR's register of bankruptcy notices by Surname + Forenames. Add a row for every borrower / purchaser being searched."}
                {(defaults?.client_persons?.length || 0) > 0 && (
                  <span className="ml-1 text-emerald-700 font-medium" data-testid="bankruptcy-autofilled-hint">
                    Auto-filled from case clients — edit as needed.
                  </span>
                )}
              </p>
              {searchedPersons.map((p, idx) => {
                const isCompany = isFullSearch && p.type === "company";
                return (
                <div key={idx} className="space-y-1" data-testid={`bankruptcy-person-row-${idx}`}>
                  {isFullSearch && (
                    <div className="flex gap-1" data-testid={`k15-person-${idx}-type`}>
                      <button type="button" onClick={() => updatePerson(idx, "type", "individual")}
                        className={`text-[10px] px-2 py-0.5 rounded font-semibold ${!isCompany ? "bg-[#1462a0] text-white" : "bg-slate-100 text-slate-600"}`}
                        data-testid={`k15-person-${idx}-type-individual`}>Individual</button>
                      <button type="button" onClick={() => updatePerson(idx, "type", "company")}
                        className={`text-[10px] px-2 py-0.5 rounded font-semibold ${isCompany ? "bg-[#1462a0] text-white" : "bg-slate-100 text-slate-600"}`}
                        data-testid={`k15-person-${idx}-type-company`}>Company</button>
                    </div>
                  )}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    {isCompany ? (
                      <div className="col-span-11 space-y-1">
                        {idx === 0 && <Label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Company name</Label>}
                        <Input
                          value={p.company_name || ""}
                          onChange={(e) => updatePerson(idx, "company_name", e.target.value)}
                          placeholder="e.g. Acme Developments Ltd"
                          className="h-9 text-sm"
                          data-testid={`k15-person-${idx}-company`}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="col-span-5 space-y-1">
                          {idx === 0 && <Label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Surname</Label>}
                          <Input
                            value={p.surname || ""}
                            onChange={(e) => updatePerson(idx, "surname", e.target.value)}
                            placeholder="e.g. Smith"
                            className="h-9 text-sm"
                            data-testid={`bankruptcy-person-${idx}-surname`}
                          />
                        </div>
                        <div className="col-span-6 space-y-1">
                          {idx === 0 && <Label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">Forename(s)</Label>}
                          <Input
                            value={p.forenames || ""}
                            onChange={(e) => updatePerson(idx, "forenames", e.target.value)}
                            placeholder="e.g. John Andrew"
                            className="h-9 text-sm"
                            data-testid={`bankruptcy-person-${idx}-forenames`}
                          />
                        </div>
                      </>
                    )}
                    <div className="col-span-1 flex justify-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePerson(idx)}
                        disabled={searchedPersons.length <= 1}
                        className="h-9 w-9 text-rose-600 hover:bg-rose-50 disabled:opacity-30"
                        title={searchedPersons.length <= 1 ? "At least one name required" : "Remove this row"}
                        data-testid={`bankruptcy-person-${idx}-remove`}
                      >
                        <Trash weight="bold" className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                );
              })}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPerson}
                className="h-8 text-xs border-dashed"
                data-testid="bankruptcy-add-person"
              >
                <Plus weight="bold" className="w-3.5 h-3.5 mr-1" />
                Add another person
              </Button>
            </div>
          )}
          {isFullSearch && (
            <div className="space-y-3 border-t border-slate-200 pt-3 mt-1" data-testid="k15-fullsearch-section">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">
                Full Search details (required by HMLR)
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  County / administrative area <span className="text-slate-400 font-normal normal-case">(comma-separate to search several)</span>
                </Label>
                <Input
                  value={counties}
                  onChange={(e) => setCounties(e.target.value)}
                  placeholder="e.g. Greater London, Kent"
                  className="h-9 text-sm"
                  data-testid="k15-counties-input"
                />
                <p className="text-[10px] text-slate-500">The historic county the land charge would have been registered against. At least one is required.</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Search from year</Label>
                  <Input
                    type="number"
                    min="1888"
                    max={String(nowYear)}
                    value={fromYear}
                    onChange={(e) => setFromYear(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="k15-from-year-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Search to year</Label>
                  <Input
                    type="number"
                    min="1888"
                    max={String(nowYear)}
                    value={toYear}
                    onChange={(e) => setToYear(e.target.value)}
                    className="h-9 text-sm"
                    data-testid="k15-to-year-input"
                  />
                </div>
              </div>
            </div>
          )}
          {isPriority && (
            <div className="space-y-3 border-t border-slate-200 pt-3 mt-1">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1462a0]">
                  Priority search details (required by HMLR)
                </div>
                {titleInfoLoading && (
                  <span className="text-[10px] text-slate-400" data-testid="title-info-loading">Looking up register…</span>
                )}
              </div>

              {/* Register snapshot card — appears when we found the title
                  in the catalogue / live HMLR data. Lets the solicitor
                  one-click adopt the canonical register values. */}
              {titleInfo?.source && (titleInfo.registered_proprietors?.length > 0 || titleInfo.property_address) && (
                <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2.5 text-xs" data-testid="title-info-card">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-wide font-semibold text-blue-900 mb-0.5 flex flex-wrap items-center gap-1.5">
                        <span>Register snapshot · title {titleInfo.title_number}</span>
                        {titleInfo.source === "oc1" && titleInfo.source_date && (() => {
                          const ageMs = Date.now() - new Date(titleInfo.source_date).getTime();
                          const ageWeeks = Math.floor(ageMs / (7 * 24 * 60 * 60 * 1000));
                          const stale = ageWeeks >= 6;
                          return (
                            <>
                              <span
                                className={`normal-case font-medium px-1.5 py-0.5 rounded text-[10px] border ${
                                  stale
                                    ? "text-amber-800 bg-amber-50 border-amber-200"
                                    : "text-emerald-700 bg-emerald-50 border-emerald-200"
                                }`}
                                data-testid="title-info-source-badge"
                                title={`Captured from a completed OC1 search on ${new Date(titleInfo.source_date).toLocaleString("en-GB")}`}
                              >
                                from OC1 · {new Date(titleInfo.source_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              </span>
                              {stale && (
                                <span
                                  className="normal-case font-semibold px-1.5 py-0.5 rounded text-[10px] border text-rose-700 bg-rose-50 border-rose-200"
                                  data-testid="title-info-stale-badge"
                                  title="This OC1 is over 6 weeks old — the register may have moved on. Consider ordering a fresh OC1 before lodging priority."
                                >
                                  {ageWeeks}w old · consider a fresh OC1
                                </span>
                              )}
                            </>
                          );
                        })()}
                        {titleInfo.source === "mock" && (
                          <span
                            className="text-amber-800 normal-case font-medium px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px]"
                            data-testid="title-info-source-badge"
                            title="No OC1 has been run on this case yet. Values come from the mock catalogue — order an OC1 to capture the real register."
                          >
                            mock — order an OC1 first
                          </span>
                        )}
                      </div>
                      {titleInfo.registered_proprietors?.length > 0 && (
                        <div className="text-blue-900">
                          <span className="text-blue-700/70">Proprietor(s): </span>
                          <strong>{titleInfo.registered_proprietors.map((p) => p.name).join(", ")}</strong>
                        </div>
                      )}
                      {titleInfo.property_address && (
                        <div className="text-blue-900">
                          <span className="text-blue-700/70">Address: </span>
                          <strong>{titleInfo.property_address}</strong>
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAutofillFromRegister}
                      className="h-7 text-[11px] shrink-0 border-blue-300 text-blue-800 hover:bg-blue-100"
                      data-testid="title-info-autofill"
                    >
                      Use these
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Search from date</Label>
                  <Input
                    type="date"
                    value={searchFromDate}
                    onChange={(e) => setSearchFromDate(e.target.value)}
                    max={today}
                    className="h-9 text-sm"
                    data-testid="search-from-date-input"
                  />
                  <p className="text-[10px] text-slate-500">The date HMLR&apos;s register was last checked — usually the date of the most recent OC1.</p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Application type</Label>
                  <Select value={applicationType} onValueChange={setApplicationType}>
                    <SelectTrigger className="h-9 text-sm" data-testid="search-application-type-select">
                      <SelectValue placeholder="What's the search for…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="purchase" data-testid="app-type-purchase">Purchase</SelectItem>
                      <SelectItem value="charge" data-testid="app-type-charge">Charge / Mortgage</SelectItem>
                      <SelectItem value="lease" data-testid="app-type-lease">Lease</SelectItem>
                      <SelectItem value="transfer_of_part" data-testid="app-type-transfer-of-part">Transfer of Part</SelectItem>
                      <SelectItem value="other" data-testid="app-type-other">Other…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {applicationType === "other" && (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Describe the application</Label>
                  <Input
                    value={applicationTypeOther}
                    onChange={(e) => setApplicationTypeOther(e.target.value)}
                    placeholder="e.g. Easement, Restrictive covenant, etc."
                    className="h-9 text-sm"
                    data-testid="search-application-type-other-input"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Registered proprietor(s) <span className="text-slate-400 font-normal normal-case">(must match the register, comma-separate joint owners)</span>
                </Label>
                <Input
                  value={proprietorNames}
                  onChange={(e) => setProprietorNames(e.target.value)}
                  placeholder="e.g. John Andrew Smith, Jane Marie Smith"
                  className={`h-9 text-sm ${proprietorsDiffer ? "border-amber-400 ring-1 ring-amber-200" : ""}`}
                  data-testid="search-proprietor-input"
                />
                {proprietorsDiffer && (
                  <p className="text-[11px] text-amber-700 flex items-start gap-1" data-testid="proprietor-divergence-warning">
                    <Warning weight="fill" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Differs from the register snapshot above. Double-check before lodging — a mismatch can void the priority entry.</span>
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Property address <span className="text-slate-400 font-normal normal-case">(as it appears on the register)</span>
                </Label>
                <textarea
                  value={propertyAddress}
                  onChange={(e) => setPropertyAddress(e.target.value)}
                  placeholder="Full property address including postcode"
                  className={`w-full min-h-[64px] text-sm rounded-md border px-3 py-2 ${addressDiffers ? "border-amber-400 ring-1 ring-amber-200" : "border-slate-200"}`}
                  data-testid="search-property-address-input"
                />
                {addressDiffers && (
                  <p className="text-[11px] text-amber-700 flex items-start gap-1" data-testid="address-divergence-warning">
                    <Warning weight="fill" className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>Differs from the register snapshot above. Lodge only if you intentionally want a different address (e.g. alternative postal form).</span>
                  </p>
                )}
              </div>

              {kind === "os2" && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  <strong>OS2 Priority Search of Part</strong> — you&apos;ll need to attach a title plan or estate plan after creating this search. Fulfilment is blocked until at least one plan is uploaded.
                </div>
              )}
            </div>
          )}
          {isSim && (
            <div className="space-y-1.5" data-testid="sim-titles-section">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">Titles expected to be disclosed</Label>
              <Input
                type="number"
                min="1"
                value={simTitles}
                onChange={(e) => setSimTitles(e.target.value)}
                className="h-9 text-sm w-32"
                data-testid="sim-titles-input"
              />
              <p className="text-[11px] text-slate-500">
                £{meta?.fee ?? 8} covers the first 5 titles; +£6 for every 10 titles (or part) beyond that. Useful for large-estate / multi-title searches.
              </p>
            </div>
          )}
          {meta && (
            <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-2.5" data-testid="search-fee-summary">
              {isBankruptcy ? (() => {
                const n = searchedPersons.filter((p) => (p.surname || "").trim() || (p.forenames || "").trim()).length || 1;
                return <><strong>{meta.category}</strong> · Fee £{meta.fee} per name × {n} {n === 1 ? "name" : "names"} = <strong>£{meta.fee * n}</strong>.</>;
              })() : isSim ? (() => {
                const t = Math.max(1, parseInt(simTitles, 10) || 1);
                return <><strong>{meta.category}</strong> · {t} {t === 1 ? "title" : "titles"} disclosed → Fee <strong>£{simFee(simTitles)}</strong>.</>;
              })() : (
                <><strong>{meta.category}</strong> · Fee £{meta.fee}.</>
              )}
              {meta.priority_days && <> Priority lasts <strong>{meta.priority_days} working days</strong> from the search timestamp.</>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="search-order-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting || !kind} className="bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="search-order-confirm">
            {submitting ? "Creating…" : "Create order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SearchRow({ search, onFulfilled, onDeleted, onUpdated, caseId }) {
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFulfil = async (queue = false) => {
    let body = {};
    if (queue) body.queue = true;
    if (search.kind === "sim") {
      const est = search.disclosed_titles || 1;
      const input = window.prompt(
        `How many titles did the Search of the Index Map disclose?\n\n` +
          `Estimated ${est}. If more are disclosed the fee is re-priced ` +
          `(£8 for the first 5 titles, +£6 per 10 thereafter) and updated on the conveyancing fees.`,
        String(est)
      );
      if (input === null) return; // cancelled
      body.disclosed_titles = Math.max(1, parseInt(input, 10) || est);
    }
    setBusy(true);
    const tid = toast.loading(queue ? `Queuing ${search.label}…` : `Running ${search.label}…`);
    try {
      const { data } = await API.post(`/searches/${search.id}/fulfil`, body);
      onFulfilled(data);
      if (data.status === "queued") {
        const when = data.queued_for ? new Date(data.queued_for).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "the next HMLR opening";
        toast.success(`Queued — will auto-submit to HM Land Registry at ${when}. Nothing has been charged.`, { id: tid, duration: 9000 });
      } else if (data.status === "processing") {
        toast.success("Lodged with HM Land Registry — fetching the result…", { id: tid });
      } else if (data.fee_adjusted) {
        toast.success(`SIM fee re-priced to £${data.fee} (${data.disclosed_titles} titles disclosed)`, { id: tid });
      } else {
        toast.success(`Result attached to Documents › Searches`, { id: tid });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fulfilment failed", { id: tid });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete ${search.label}?`)) return;
    try {
      await API.delete(`/searches/${search.id}`);
      onDeleted(search.id);
      toast.success("Search deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const handleVoid = async () => {
    const reason = window.prompt(
      `Void ${search.label}?\n\nThe result PDF stays on file for audit, but its fee will be removed from the conveyancing fees / completion statement.\n\nReason (optional):`,
      ""
    );
    if (reason === null) return; // cancelled
    try {
      const { data } = await API.post(`/searches/${search.id}/void`, { reason });
      onUpdated?.(data);
      toast.success("Search voided — fee removed from conveyancing fees");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Void failed");
    }
  };

  const handleDownload = async () => {
    if (!search.result_document_id) return;
    try {
      const resp = await API.get(`/documents/${search.result_document_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${search.kind}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const handlePlanUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post(`/searches/${search.id}/plan-attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated?.(data);
      toast.success(`Plan attached: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Plan upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handlePlanDelete = async (attachmentId) => {
    if (!window.confirm("Remove this plan attachment?")) return;
    try {
      const { data } = await API.delete(`/searches/${search.id}/plan-attachments/${attachmentId}`);
      onUpdated?.(data);
      toast.success("Plan removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove plan");
    }
  };

  /* OS2 priority searches need a plan to be lodged. Drives the
     "Fulfil" button state + the warning chip in the row. */
  const isOs2 = search.kind === "os2";
  const isPriority = search.kind === "os1" || search.kind === "os2";
  const isDone = search.status === "completed" || search.status === "received";
  const isVoided = search.status === "voided";
  const isProcessing = search.status === "processing";
  const isQueued = search.status === "queued";
  const isFailed = search.status === "failed";
  const planAttachments = search.plan_attachments || [];
  const blockedByPlan = isOs2 && planAttachments.length === 0 && search.status !== "completed";

  const APPLICATION_TYPE_LABEL = {
    purchase: "Purchase",
    charge: "Charge / Mortgage",
    lease: "Lease",
    transfer_of_part: "Transfer of Part",
    other: search.application_type_other || "Other",
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white flex flex-col gap-3" data-testid={`search-row-${search.kind}-${search.id.slice(0, 8)}`}>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-900">{search.label}</h4>
            <StatusPill status={search.status} />
            <PriorityCountdown expiresAt={search.priority_expires_at} />
            {blockedByPlan && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="search-needs-plan-pill">
                Needs plan
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1 space-x-3">
            {search.title_number && <span>Title <span className="font-mono text-slate-700">{search.title_number}</span></span>}
            {search.applicant_name && <span>Applicant <strong>{search.applicant_name}</strong></span>}
            <span>£{search.fee}</span>
            {search.fee_adjusted && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" title={`Re-priced from £${search.estimated_fee} — ${search.disclosed_titles} titles disclosed`} data-testid={`search-fee-adjusted-${search.id.slice(0, 8)}`}>
                Fee re-priced
              </span>
            )}
            {search.completed_at && <span>Completed {new Date(search.completed_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>}
          </div>
          {search.status === "failed" && search.error_detail && (
            <p className="text-[11px] text-rose-600 mt-1" data-testid={`search-error-${search.id.slice(0, 8)}`}>{search.error_detail}</p>
          )}
          {isProcessing && (
            <p className="text-[11px] text-indigo-600 mt-1" data-testid={`search-processing-note-${search.id.slice(0, 8)}`}>Lodged with HM Land Registry — this can take up to a minute. It'll update automatically.</p>
          )}
          {isQueued && (
            <p className="text-[11px] text-violet-700 mt-1" data-testid={`search-queued-note-${search.id.slice(0, 8)}`}>
              Queued — will auto-submit to HM Land Registry at{" "}
              <strong>{search.queued_for ? new Date(search.queued_for).toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "the next opening (06:30, Mon–Fri)"}</strong>. Nothing is charged until it runs.
            </p>
          )}
          {isPriority && (search.search_from_date || search.application_type || search.proprietor_names?.length || search.property_address) && (
            <div className="mt-2 text-[11px] text-slate-600 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5" data-testid={`search-priority-meta-${search.id.slice(0, 8)}`}>
              {search.search_from_date && <div><span className="text-slate-400">Search from:</span> <strong>{new Date(search.search_from_date).toLocaleDateString("en-GB")}</strong></div>}
              {search.application_type && <div><span className="text-slate-400">Application:</span> <strong>{APPLICATION_TYPE_LABEL[search.application_type] || search.application_type}</strong></div>}
              {search.proprietor_names?.length > 0 && <div className="sm:col-span-2"><span className="text-slate-400">Proprietor(s):</span> <strong>{search.proprietor_names.join(", ")}</strong></div>}
              {search.property_address && <div className="sm:col-span-2"><span className="text-slate-400">Property:</span> <strong>{search.property_address}</strong></div>}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!isDone && !isVoided && (
            <Button
              size="sm"
              onClick={() => handleFulfil(false)}
              disabled={busy || blockedByPlan || isProcessing}
              title={blockedByPlan ? "Attach a title/estate plan first" : ""}
              className="bg-[#1462a0] hover:bg-[#10507f] text-white h-8 disabled:opacity-50"
              data-testid={`search-fulfil-${search.id.slice(0, 8)}`}
            >
              {isProcessing ? "Processing…" : (busy ? "Running…" : (isFailed ? "Retry" : (isQueued ? "Submit now" : "Fulfil")))}
            </Button>
          )}
          {!isDone && !isVoided && !isProcessing && !isQueued && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleFulfil(true)}
              disabled={busy || blockedByPlan}
              title="Hold the order and auto-submit when HMLR next opens (06:30–23:00, Mon–Fri)"
              className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50"
              data-testid={`search-queue-${search.id.slice(0, 8)}`}
            >
              <Clock weight="bold" className="w-3.5 h-3.5 mr-1" /> Queue for opening
            </Button>
          )}
          {search.result_document_id && (
            <Button size="sm" variant="outline" onClick={handleDownload} className="h-8" data-testid={`search-download-${search.id.slice(0, 8)}`}>
              <FileText weight="bold" className="w-3.5 h-3.5 mr-1" /> Download
            </Button>
          )}
          {isDone && (
            <Button size="sm" variant="ghost" onClick={handleVoid} className="text-amber-700 hover:bg-amber-50 h-8" data-testid={`search-void-${search.id.slice(0, 8)}`}>
              Void
            </Button>
          )}
          {!isDone && !isVoided && !isProcessing && (
            <Button size="sm" variant="ghost" onClick={handleDelete} className="text-rose-600 h-8" data-testid={`search-delete-${search.id.slice(0, 8)}`}>
              <Trash weight="bold" className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* OS2 plan-attachment subsection — only renders for OS2 searches
          that haven't been fulfilled yet. After completion the attachments
          stay listed but the upload control is hidden. */}
      {isOs2 && (
        <div className="border-t border-slate-100 pt-2" data-testid={`search-plans-${search.id.slice(0, 8)}`}>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              Title / estate plans ({planAttachments.length})
            </Label>
            {search.status !== "completed" && (
              <label className="text-xs text-[#1462a0] font-semibold cursor-pointer hover:underline">
                {uploading ? "Uploading…" : "+ Attach plan"}
                <input
                  type="file"
                  className="hidden"
                  accept="application/pdf,image/tiff,image/png,image/jpeg"
                  onChange={(e) => { handlePlanUpload(e.target.files?.[0]); e.target.value = ""; }}
                  disabled={uploading}
                  data-testid={`search-plan-upload-${search.id.slice(0, 8)}`}
                />
              </label>
            )}
          </div>
          {planAttachments.length === 0 ? (
            <p className="text-[11px] text-slate-400 italic">No plans attached yet — OS2 cannot be lodged without one.</p>
          ) : (
            <ul className="text-xs space-y-1">
              {planAttachments.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded px-2 py-1.5" data-testid={`search-plan-row-${p.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText weight="duotone" className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="truncate text-slate-700">{p.filename}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{(p.size / 1024).toFixed(0)} KB</span>
                  </div>
                  {search.status !== "completed" && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handlePlanDelete(p.id)}
                      className="h-6 w-6 text-rose-600 hover:bg-rose-50 shrink-0"
                      data-testid={`search-plan-delete-${p.id}`}
                    >
                      <Trash weight="bold" className="w-3 h-3" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function SearchesTab({ caseId, caseData, isSolicitor, externalAction }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], by_category: {}, summary: {} });
  const [catalogue, setCatalogue] = useState([]);
  const [orderOpen, setOrderOpen] = useState(false);
  const [ocOpen, setOcOpen] = useState(false);

  // React to chip-clicks from the parent Authorities workflow:
  //   "hmlr:office_copies"   → opens the OC wizard
  //   "hmlr:priority"        → opens the generic OS/K dialog
  //   "hmlr:bankruptcy"      → opens the generic OS/K dialog
  // We key off externalAction.ts so the same action can fire twice.
  useEffect(() => {
    if (!externalAction?.key) return;
    if (externalAction.key === "hmlr:office_copies") {
      setOcOpen(true);
    } else if (externalAction.key === "hmlr:priority" || externalAction.key === "hmlr:bankruptcy") {
      setOrderOpen(true);
    }
  }, [externalAction?.key, externalAction?.ts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, cat] = await Promise.all([
        API.get(`/cases/${caseId}/searches`),
        API.get(`/hmlr-searches/catalogue`),
      ]);
      setData(list.data);
      setCatalogue(cat.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load searches");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // While any search is being fulfilled live (status='processing'), poll the
  // list so the row flips to Completed/Failed on its own without a refresh.
  const hasProcessing = useMemo(
    () => (data?.items || []).some((x) => x.status === "processing"),
    [data]
  );
  useEffect(() => {
    if (!hasProcessing) return undefined;
    const t = setInterval(async () => {
      try {
        const { data: list } = await API.get(`/cases/${caseId}/searches`);
        setData(list);
      } catch { /* transient — keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [hasProcessing, caseId]);

  const handleCreated = (s) => setData((d) => ({ ...d, items: [...(d?.items || []), s], by_category: groupByCategory([...(d?.items || []), s]) }));
  const handleFulfilled = (s) => setData((d) => {
    const items = (d?.items || []).map((x) => x.id === s.id ? s : x);
    return { ...d, items, by_category: groupByCategory(items) };
  });
  const handleDeleted = (id) => setData((d) => {
    const items = (d?.items || []).filter((x) => x.id !== id);
    return { ...d, items, by_category: groupByCategory(items) };
  });

  const categories = useMemo(() => Object.keys(data?.by_category || {}), [data]);
  const defaults = useMemo(() => ({
    case_id: caseId,
    title_number: caseData?.title_number || "",
    applicant_name: (caseData?.clients || []).map((c) => c.name).filter(Boolean).join(" & "),
    // Structured client names for K15/K16 auto-suggest. Splits each
    // client's `name` field into last-word=surname / rest=forenames —
    // solicitor can edit before submitting so a mis-split is a 5-second
    // fix rather than a blocker.
    client_persons: (caseData?.clients || [])
      .map((c) => (c.name || "").trim())
      .filter(Boolean)
      .map((name) => {
        const parts = name.split(/\s+/);
        if (parts.length >= 2) return { surname: parts[parts.length - 1], forenames: parts.slice(0, -1).join(" ") };
        return { surname: parts[0] || "", forenames: "" };
      }),
  }), [caseData, caseId]);

  // The original generic order dialog now only handles non-OC searches —
  // OC1/OC2/oc_filed_deed go through the dedicated OrderOfficeCopiesDialog
  // wizard so the solicitor gets address lookup + full document catalogue.
  const nonOcCatalogue = useMemo(() => catalogue.filter((c) => c.category !== "Office Copies"), [catalogue]);

  if (loading) return <div className="text-sm text-slate-500 p-6">Loading searches…</div>;

  const empty = !data?.items?.length;

  return (
    <div className="space-y-5" data-testid="searches-tab">
      {/* Header / summary */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>HMLR Searches</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Office Copies (OC1/OC2), Priority Searches (OS1/OS2), and Land Charges Bankruptcy (K15/K16). Results attach automatically to Documents › Searches.
            </p>
          </div>
          {isSolicitor && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setOcOpen(true)} variant="outline" className="border-[#1462a0]/40 text-[#1462a0] hover:bg-[#1462a0]/10" data-testid="searches-order-oc-btn">
                <House weight="bold" className="w-4 h-4 mr-1" /> Order Office Copies
              </Button>
              <Button onClick={() => setOrderOpen(true)} className="bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="searches-order-btn">
                <Plus weight="bold" className="w-4 h-4 mr-1" /> Order priority / bankruptcy
              </Button>
            </div>
          )}
        </div>
        {!empty && (
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mt-4">
            <SummaryStat label="Total" value={data.summary?.total} testid="searches-sum-total" />
            <SummaryStat label="Pending" value={data.summary?.pending} testid="searches-sum-pending" />
            <SummaryStat label="Queued" value={data.summary?.queued} tone="violet" testid="searches-sum-queued" />
            <SummaryStat label="Ordered" value={data.summary?.ordered} testid="searches-sum-ordered" />
            <SummaryStat label="Completed" value={data.summary?.completed} tone="emerald" testid="searches-sum-completed" />
            <SummaryStat label="Priority active" value={data.summary?.priority_active} tone="amber" testid="searches-sum-priority" />
          </div>
        )}
      </div>

      {empty ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-lg p-10 text-center">
          <MagnifyingGlass weight="duotone" className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-sm text-slate-600">No searches ordered yet.</p>
          <p className="text-xs text-slate-500 mt-1">Click <strong>Order new search</strong> to run OCs, priority or bankruptcy searches.</p>
        </div>
      ) : (
        categories.map((cat) => {
          const CatIcon = CATEGORY_ICONS[cat] || Bank;
          const list = data.by_category[cat] || [];
          return (
            <div key={cat} className="space-y-2">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-slate-600">
                <CatIcon weight="duotone" className="w-4 h-4 text-[#1462a0]" />
                {cat}
                <span className="text-slate-400 font-normal normal-case">({list.length})</span>
              </div>
              <div className="space-y-2">
                {list.map((s) => (
                  <SearchRow key={s.id} search={s} caseId={caseId} onFulfilled={handleFulfilled} onDeleted={handleDeleted} onUpdated={handleFulfilled} />
                ))}
              </div>
            </div>
          );
        })
      )}

      <OrderSearchDialog
        open={orderOpen}
        onOpenChange={setOrderOpen}
        catalogue={nonOcCatalogue}
        defaults={defaults}
        onCreated={handleCreated}
      />

      <OrderOfficeCopiesDialog
        open={ocOpen}
        onOpenChange={setOcOpen}
        caseId={caseId}
        caseData={caseData}
        onCreated={handleCreated}
      />
    </div>
  );
}

function SummaryStat({ label, value, tone = "slate", testid }) {
  const cls = tone === "emerald" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : tone === "amber" ? "text-amber-700 bg-amber-50 border-amber-200"
    : tone === "rose" ? "text-rose-700 bg-rose-50 border-rose-200"
    : tone === "violet" ? "text-violet-700 bg-violet-50 border-violet-200"
    : "text-slate-700 bg-white border-slate-200";
  return (
    <div className={`border rounded-md px-3 py-2 ${cls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-lg font-semibold">{value ?? 0}</div>
    </div>
  );
}

function groupByCategory(items) {
  const out = {};
  items.forEach((it) => {
    const k = it.category || "Other";
    if (!out[k]) out[k] = [];
    out[k].push(it);
  });
  return out;
}
